/**
 * Quote queue hooks (#2652).
 *
 * The follow-up list for quotations: work orders in status `quote`, each in
 * one bucket derived from its sends (#2651) and validity. Served by
 * `GET /work-orders/quote-queue` and `/quote-queue/counts`; the dashboard
 * widget reads the same buckets from `/dashboard/stats`.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import { fromOffsetResponse, usePagedQuery } from './usePagedQuery';

/** Mirrors QUOTE_QUEUE_BUCKETS in staff-api `lib/quote-queue.ts`, in working order. */
export const QUOTE_QUEUE_BUCKETS = ['accepted', 'expiring', 'unsent', 'awaiting', 'expired', 'rejected'] as const;
export type QuoteQueueBucket = (typeof QUOTE_QUEUE_BUCKETS)[number];

export const QUOTE_QUEUE_SORT_FIELDS = ['priority', 'quoteExpiresAt', 'sentAt', 'createdAt', 'workOrderNumber'] as const;
export type QuoteQueueSortField = (typeof QUOTE_QUEUE_SORT_FIELDS)[number];

export interface QuoteQueueSend {
  sentAt: string;
  sentVia: 'email' | 'whatsapp' | string;
  recipient: string;
  reminderCount: number;
  respondedAt: string | null;
  response: 'accepted' | 'rejected' | null;
  rejectionReason: string | null;
  customerComment: string | null;
}

export interface QuoteQueueItem {
  id: string;
  workOrderNumber: string | null;
  status: string;
  bucket: QuoteQueueBucket;
  customerName: string;
  customerPhone: string | null;
  vehicleRegNumber: string;
  vehicleMake: string | null;
  vehicleModel: string | null;
  serviceNames: string;
  quoteExpiresAt: string | null;
  createdAt: string;
  latestSend: QuoteQueueSend | null;
}

export type QuoteQueueCounts = Record<QuoteQueueBucket, number> & { open: number };

export interface QuoteQueueFilters {
  /** Omitted = every open bucket (all but `rejected`). */
  bucket?: QuoteQueueBucket;
  q?: string;
}

export function usePagedQuoteQueue(filters: QuoteQueueFilters = {}) {
  return usePagedQuery({
    // Under the work-order list key, so send/accept/reject invalidations refresh the queue too.
    queryKey: [...queryKeys.workOrders.lists(), 'quoteQueue'],
    filters,
    defaultSort: { sortBy: 'priority', sortDir: 'asc' },
    staleTime: 15_000,
    fetchPage: async ({ filters: pageFilters, limit, offset, sortBy, sortDir }) => {
      const params: Record<string, string | number | undefined> = { ...pageFilters, limit, offset };
      if (sortBy !== undefined) params.sortBy = sortBy;
      if (sortDir !== undefined) params.sortDir = sortDir;
      const res = await api.get<{ data: QuoteQueueItem[]; pagination: { total: number } }>(
        '/work-orders/quote-queue',
        { params },
      );
      return fromOffsetResponse(res);
    },
  });
}

export function useQuoteQueueCounts() {
  return useQuery({
    queryKey: queryKeys.workOrders.stats({ view: 'quoteQueue' }),
    queryFn: async () => (await api.get<{ data: QuoteQueueCounts }>('/work-orders/quote-queue/counts')).data,
    staleTime: 15_000,
  });
}
